import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { Icons } from '../../../shared/helpers/icons';
import { IUserInfo } from '../../../shared/models/contact';
import { UserDataService } from '../../../shared/services/userData.service';
import { ITransportationNeeds } from '../../../shared/models/transportationNeeds';
import { TransportationNeedsService } from '../../../shared/services/transportationNeeds.service';

@Component({
  selector: 'transportation-needs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    IntroductionComponent,
    PopupMessageComponent,
    SpinnerComponent,
    PrvNextButtonsComponent
  ],
  templateUrl: './transportation-needs.component.html',
  styleUrl: './transportation-needs.component.css'
})
export class TransportationNeedsComponent implements OnInit {

  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;

  data: ITransportationNeeds = {};

  // "Types of Buses Needed" — one count per bus type.
  busTypes: { key: keyof ITransportationNeeds; label: string; description: string }[] = [
    { key: 'regular', label: 'Regular', description: 'Seating Capacity: 66 elementary students; 44 middle/high' },
    { key: 'special_needs', label: 'Special Needs', description: 'may not be equipped for wheelchairs' },
    { key: 'lift', label: 'Lift', description: 'wheelchair accessible' },
  ];

  loading: boolean = false;
  messageModal: string | null = null;
  popupMessage: string | null = null;

  constructor(
    private userDataService: UserDataService,
    private transportationNeedsService: TransportationNeedsService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadData();
  }

  loadData(): void {
    if (this.currentBuildingId) {
      this.loading = true;
      this.transportationNeedsService.getTransportationNeeds(this.currentBuildingId).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.has_error) {
            this.data = response.data || {};
          }
        },
        error: (err) => {
          this.loading = false;
          this.messageModal = 'unsuccess';
          this.popupMessage = err.message;
        }
      });
    }
  }

  onSubmit(): void {
    if (this.currentBuildingId && this.data) {
      this.loading = true;
      const payload: Partial<ITransportationNeeds> = {
        regular: this.data.regular ?? null as any,
        special_needs: this.data.special_needs ?? null as any,
        lift: this.data.lift ?? null as any,
      };

      this.transportationNeedsService.updateTransportationNeeds(this.currentBuildingId, payload).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.has_error) {
            this.messageModal = 'success';
            this.popupMessage = 'This occurred after updating Transportation Needs.';
            this.loadData();
          } else {
            this.messageModal = 'unsuccess';
            this.popupMessage = response.message;
          }
        },
        error: (err) => {
          this.loading = false;
          this.messageModal = 'unsuccess';
          this.popupMessage = err.message;
        }
      });
    }
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }

  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }
}
